import React from 'react';
import {useSearchParams} from 'react-router-dom';
import PropTypes from 'prop-types';
import {format} from 'date-fns';
import {fr} from 'date-fns/locale';

// @mui
import {styled, alpha, useTheme} from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Alert from '@mui/material/Alert';
import Fade from '@mui/material/Fade';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';

// icons
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import TimerIcon from '@mui/icons-material/Timer';
import AddTaskIcon from '@mui/icons-material/AddTask';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

// constants
import {SERVICE_TYPES} from '../constants/reservation';

// utils
import {isInIframe} from '../../../utils/iframeUtils';


// ----------------------------------------------------------------------

// Conteneur de confirmation avec hauteur max et défilement
const ConfirmationContainer = styled(Box)(({theme}) => ({
  padding: theme.spacing(3),
  maxWidth: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  overflowY: 'auto', // Ajout de la barre de défilement verticale
  scrollBehavior: 'smooth',
}));

const SuccessIconWrapper = styled(Box)(({theme}) => ({
  width: 80,
  height: 80,
  display: 'flex',
  borderRadius: '50%',
  alignItems: 'center',
  justifyContent: 'center',
  /* backgroundColor: alpha(theme.palette.success.main, 0.12), */
  color: theme.palette.success.dark,
  margin: '0 auto 24px',
  /* boxShadow: `0 8px 20px ${alpha(theme.palette.success.main, 0.3)}`, */
}));

const InfoCard = styled(Paper)(({theme}) => ({
  padding: theme.spacing(2.5),
  marginBottom: theme.spacing(2.5),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.background.paper, 0.6),
  boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.04)}`,
  width: '100%', // S'assurer que la carte prend toute la largeur
}));

const InfoRow = styled(Box)(({theme}) => ({
  'display': 'flex',
  'alignItems': 'flex-start',
  'marginBottom': theme.spacing(2),
  '&:last-child': {
    marginBottom: 0,
  },
}));

const InfoIcon = styled(Box)(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 32,
  height: 32,
  borderRadius: '50%',
  backgroundColor: alpha(theme.palette.primary.main, 0.08),
  color: theme.palette.primary.main,
  marginRight: theme.spacing(1.5),
  flexShrink: 0,
}));

const ButtonsContainer = styled(Box)(({theme}) => ({
  display: 'flex',
  justifyContent: 'center',
  marginTop: theme.spacing(1),
  padding: theme.spacing(2, 0),
  backgroundColor: theme.palette.background.paper,
  position: 'sticky', // Position sticky pour rester visible
  bottom: 0,
  left: 0,
  right: 0,
  zIndex: 10,
  borderTop: `1px solid ${theme.palette.divider}`,
  width: '100%',
}));

const ActionButton = styled(Button)(({theme, color = 'primary'}) => ({
  'borderRadius': theme.shape.borderRadius * 4,
  'padding': theme.spacing(1, 3),
  'margin': theme.spacing(0, 1),
  'textTransform': 'none',
  'fontWeight': 600,
  'boxShadow': color === 'primary' ?
    `0 4px 10px ${alpha(theme.palette.primary.main, 0.3)}` :
    `0 4px 10px ${alpha(theme.palette.text.primary, 0.1)}`,
  'transition': theme.transitions.create(['transform', 'box-shadow']),
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: color === 'primary' ?
      `0 6px 14px ${alpha(theme.palette.primary.main, 0.4)}` :
      `0 6px 14px ${alpha(theme.palette.text.primary, 0.15)}`,
  },
}));

// ----------------------------------------------------------------------

export function ConfirmationStep({formData, config, onReset, onClose}) {
  const theme = useTheme();
  const embedded = isInIframe();
  const mode = 'fixed';
  const [searchParams] = useSearchParams();
  const urlMode = searchParams.get('mode');
  const displayMode = (urlMode || mode).toLowerCase().trim();
  const serviceDisplay = formData.service === SERVICE_TYPES.LUNCH ? 'Midi' : 'Soir';

  // Get service duration from config (simplified for compact version)
  const getServiceDuration = () => {
    return config?.globalParameters?.defaultDuration || 120;
  };

  return (
    <Fade in timeout={400}>
      <Box sx={{display: 'flex', flexDirection: 'column', height: '100%'}}>
        <ConfirmationContainer>
          <SuccessIconWrapper>
            <CheckCircleIcon sx={{fontSize: 48, boxShadow: `0 0px 40px 7px ${alpha(theme.palette.success.main, 0.3)}`, backgroundColor: alpha(theme.palette.success.main, 0.12), borderRadius: '100%'}} />
          </SuccessIconWrapper>

          <Typography variant="h5" gutterBottom align="center" sx={{fontWeight: 600}}>
            Réservation confirmée !
          </Typography>

          <Typography variant="subtitle1" color="text.secondary" align="center" sx={{mb: 4}}>
            {format(formData.date, 'EEEE d MMMM', {locale: fr})} à {formData.timeSlot}
          </Typography>

          <Stack spacing={2} sx={{width: '100%', mb: 4}} border={1} borderColor="divider" borderRadius={2} padding={4}>
            <Typography variant="subtitle1" sx={{mb: 1, fontWeight: 600, display: 'flex', alignItems: 'center'}}>
              <RestaurantIcon sx={{mr: 1, fontSize: '1.2rem'}} />
              Détails de votre réservation
            </Typography>
            <Divider sx={{mb: 2}} />

            <Stack direction='row'>
              <InfoIcon>
                <RestaurantIcon fontSize="small" />
              </InfoIcon>
              <Box>
                <Typography variant="body2" sx={{fontWeight: 600}}>Service</Typography>
                <Typography variant="body2">{serviceDisplay}</Typography>
              </Box>
            </Stack>

            <Stack direction='row'>
              <InfoIcon>
                <PeopleAltIcon fontSize="small" />
              </InfoIcon>
              <Box>
                <Typography variant="body2" sx={{fontWeight: 600}}>Personnes</Typography>
                <Typography variant="body2">
                  {formData.partySize} {formData.partySize === 1 ? 'personne' : 'personnes'}
                </Typography>
              </Box>
            </Stack>

            <Stack direction='row'>
              <InfoIcon>
                <CalendarMonthIcon fontSize="small" />
              </InfoIcon>
              <Box>
                <Typography variant="body2" sx={{fontWeight: 600}}>Date</Typography>
                <Typography variant="body2">
                  {format(formData.date, 'EEEE d MMMM yyyy', {locale: fr})}
                </Typography>
              </Box>
            </Stack>

            <Stack direction='row'>
              <InfoIcon>
                <AccessTimeIcon fontSize="small" />
              </InfoIcon>
              <Box>
                <Typography variant="body2" sx={{fontWeight: 600}}>Heure</Typography>
                <Typography variant="body2">{formData.timeSlot}</Typography>
              </Box>
            </Stack>

            <InfoRow>
              <InfoIcon>
                <TimerIcon fontSize="small" />
              </InfoIcon>
              <Box>
                <Typography variant="body2" sx={{fontWeight: 600}}>Durée estimée</Typography>
                <Typography variant="body2">{getServiceDuration()} minutes</Typography>
              </Box>
            </InfoRow>
          </Stack>

          <Stack spacing={2} sx={{width: '100%', mb: 4}} border={1} borderColor="divider" borderRadius={2} padding={4}>
            <Typography variant="subtitle1" sx={{mb: 1, fontWeight: 600, display: 'flex', alignItems: 'center'}}>
              <PersonIcon sx={{mr: 1, fontSize: '1.2rem'}} />
              Informations client
            </Typography>
            <Divider sx={{mb: 2}} />

            <Stack direction='row'>
              <InfoIcon>
                <PersonIcon fontSize="small" />
              </InfoIcon>
              <Box>
                <Typography variant="body2" sx={{fontWeight: 600}}>Nom</Typography>
                <Typography variant="body2">{formData.firstName} {formData.lastName}</Typography>
              </Box>
            </Stack>

            <Stack direction='row'>
              <InfoIcon>
                <EmailIcon fontSize="small" />
              </InfoIcon>
              <Box>
                <Typography variant="body2" sx={{fontWeight: 600}}>Email</Typography>
                <Typography variant="body2">{formData.email}</Typography>
              </Box>
            </Stack>

            <Stack direction='row'>
              <InfoIcon>
                <LocalPhoneIcon fontSize="small" />
              </InfoIcon>
              <Box>
                <Typography variant="body2" sx={{fontWeight: 600}}>Téléphone</Typography>
                <Typography variant="body2">{formData.phone}</Typography>
              </Box>
            </Stack>
          </Stack>

          {/* Afficher les détails supplémentaires s'ils existent */}
          {(formData.occasion || formData.specialRequests) && (
            <InfoCard elevation={0}>
              <Typography variant="subtitle1" sx={{mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center'}}>
                <AddTaskIcon sx={{mr: 1, fontSize: '1.2rem'}} />
                Détails supplémentaires
              </Typography>
              <Divider sx={{mb: 2}} />

              {formData.occasion && (
                <InfoRow>
                  <InfoIcon>
                    <AddTaskIcon fontSize="small" />
                  </InfoIcon>
                  <Box>
                    <Typography variant="body2" sx={{fontWeight: 600}}>Occasion</Typography>
                    <Typography variant="body2">{formData.occasion}</Typography>
                  </Box>
                </InfoRow>
              )}

              {formData.specialRequests && (
                <InfoRow>
                  <InfoIcon>
                    <AddTaskIcon fontSize="small" />
                  </InfoIcon>
                  <Box>
                    <Typography variant="body2" sx={{fontWeight: 600}}>Demandes spéciales</Typography>
                    <Typography variant="body2">{formData.specialRequests}</Typography>
                  </Box>
                </InfoRow>
              )}
            </InfoCard>
          )}

          <Alert
            severity="success"
            icon={<AddTaskIcon />}
            sx={{width: '100%', mb: 3}}
            variant="outlined"
          >
            Un email de confirmation a été envoyé à votre adresse.
          </Alert>
        </ConfirmationContainer>

        <ButtonsContainer>
          <ActionButton
            variant="contained"
            color="primary"
            onClick={onReset}
            startIcon={<RestartAltIcon />}
          >
            Nouvelle réservation
          </ActionButton>

          {onClose && embedded && displayMode === 'floating' && (
            <ActionButton
              variant="outlined"
              onClick={onClose}
              color="inherit"
            >
              Fermer
            </ActionButton>
          )}
        </ButtonsContainer>
      </Box>
    </Fade>
  );
}

ConfirmationStep.propTypes = {
  formData: PropTypes.object.isRequired,
  config: PropTypes.object,
  onReset: PropTypes.func.isRequired,
  onClose: PropTypes.func,
};
